import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Reactive read of every cached entity under a given key prefix.
 *
 * **What it builds.** TanStack Query's cache is per-query-key; there's
 * no native entity table. This helper assembles one virtually by walking
 * every cached query whose key starts with the supplied prefix, pulling
 * each entry's `data`, and filtering out the empty slots. Re-renders
 * when any matching slot changes (added, updated, or removed).
 *
 * **No fetch fires.** This is purely a cache subscription. Consumers use
 * it to derive views from entities that other queries (typically the
 * cluster sideload from a list/detail fetch) have populated.
 *
 * **Stable identity for unchanged data.** `useSyncExternalStore` requires
 * `getSnapshot` to return the same reference when the underlying data
 * hasn't changed. We compare element-wise against the previous snapshot
 * via a ref and return the cached reference when nothing differs.
 *
 * **`select` for derived views.** Pass `select` to filter / map / reshape.
 * Runs against the cached array. **Callers must memoize `select`** —
 * either via `useCallback` or by hoisting to module scope — otherwise
 * a fresh function reference every render forces the result through
 * `select` again and the hook returns a new array each time, defeating
 * the snapshot identity stability the rest of the hook works hard to
 * preserve.
 *
 *   const allCampaigns = useAllCached<Campaign>(campaignKeys.details(wsId));
 *
 *   // good — select hoisted out
 *   const filterByTag = useCallback(
 *     (cs: readonly Campaign[]) => cs.filter((c) => c.tag_ids.includes(tagId)),
 *     [tagId],
 *   );
 *   const taggedCampaigns = useAllCached<Campaign, Campaign[]>(
 *     campaignKeys.details(wsId),
 *     { select: filterByTag },
 *   );
 *
 * **Cardinality budget.** `subscribe` re-runs `buildSnapshot` (an
 * O(matching-slots) `findAll` walk) on every matching cache event.
 * Designed for prefixes with **up to a few hundred slots** — the
 * pilot's `campaign.details` family is well under that. Don't reach for
 * this hook for high-volume entities like people / events / deliveries
 * where thousands of detail slots can land in cache; pair those with a
 * dedicated paginated list query instead.
 *
 * The same `buildSnapshot` walk also runs **synchronously during the
 * first render at a new prefix** (e.g. on workspace switch) so the new
 * tree doesn't briefly serve the previous prefix's snapshot. Render-
 * phase cost = O(matching-slots). At the cardinality budget above this
 * is sub-millisecond; well over it, the dev-only warn fires from both
 * the render path and the subscribe path.
 */
export interface UseAllCachedOptions<TEntity, TSelected = readonly TEntity[]> {
  select?: (entities: readonly TEntity[]) => TSelected;
}

function keyHasPrefix(key: readonly unknown[], prefix: readonly unknown[]): boolean {
  if (key.length < prefix.length) return false;
  for (let index = 0; index < prefix.length; index++) {
    if (key[index] !== prefix[index]) return false;
  }
  return true;
}

function snapshotsAreIdentical<T>(previous: readonly T[] | null, next: readonly T[]): boolean {
  if (previous == null || previous.length !== next.length) return false;
  return previous.every((item, index) => item === next[index]);
}

/** Cache events that can change the materialized data we project from `findAll`. */
const CACHE_DATA_EVENTS = new Set(['added', 'removed', 'updated']);

function isCacheDataEvent(type: string): boolean {
  return CACHE_DATA_EVENTS.has(type);
}

/**
 * Stable empty-array sentinel used as the `getSnapshot` fallback when
 * the snapshot ref hasn't been built yet. `useSyncExternalStore` requires
 * `getSnapshot` to return the same reference for unchanged data; a fresh
 * `[]` per call would violate that contract and trigger render loops.
 */
const EMPTY: readonly never[] = [];

/**
 * Dev-only warning threshold. The hook scans every matching slot on
 * every cache event under the prefix; once a workspace caches more
 * than this many slots the convention starts to bite (see the JSDoc's
 * cardinality budget). Logged once per (prefix, threshold-breach) pair
 * so a heavy run doesn't spam the console.
 */
const CARDINALITY_WARN_THRESHOLD = 500;
// Module-scope `Set` of `prefixKey`s already warned. Dev-only — not
// production memory pressure: bounded by the number of distinct
// prefixes the user mounts in a single tab session (typically <10),
// and the strings are short. Survives HMR but a full reload resets it.
const cardinalityWarned = new Set<string>();

function maybeWarnCardinality(prefixKey: string, size: number): void {
  if (!import.meta.env.DEV) return;
  if (size <= CARDINALITY_WARN_THRESHOLD) return;
  if (cardinalityWarned.has(prefixKey)) return;
  cardinalityWarned.add(prefixKey);
  // eslint-disable-next-line no-console -- dev-only signal that the cardinality budget is exceeded
  console.warn(
    `[useAllCached] prefix ${prefixKey} has ${size} cached slots; ` +
      `over the ${CARDINALITY_WARN_THRESHOLD}-slot budget. Consider a paginated list query instead.`,
  );
}

export function useAllCached<TEntity, TSelected = readonly TEntity[]>(
  prefix: readonly unknown[],
  options?: UseAllCachedOptions<TEntity, TSelected>,
): TSelected {
  const qc = useQueryClient();
  // Serialize for stable hook deps — the prefix array's identity changes
  // each render, but its contents typically don't. Assumes prefix
  // elements are JSON-serializable primitives (string/number/boolean) —
  // matches the convention used by `defineEntity`'s key factories.
  // Don't pass `Date`, `undefined`, or non-deterministic objects.
  const prefixKey = JSON.stringify(prefix);

  // The snapshot is the cached list of entities under `prefix`. We
  // maintain it in `subscribe` (only when matching cache events fire)
  // and serve it from a ref in `getSnapshot` so that `getSnapshot` is
  // O(1) on every render — the O(cache size) `findAll` + `map` + `filter`
  // scan only runs in response to cache events, not on unrelated renders.
  const snapshotRef = useRef<readonly TEntity[] | null>(null);
  // Track which prefix the snapshot belongs to. When the prefix changes
  // (e.g. workspace switch), we rebuild eagerly instead of serving the
  // previous workspace's data until the next matching cache event.
  const snapshotPrefixRef = useRef<string | null>(null);

  const buildSnapshot = useCallback(
    (): readonly TEntity[] =>
      qc
        .getQueryCache()
        .findAll({ queryKey: prefix })
        .reduce<TEntity[]>((result, q) => {
          const d = q.state.data as TEntity | undefined;
          if (d != null) result.push(d);
          return result;
        }, []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefix captured via prefixKey
    [qc, prefixKey],
  );

  // Build the snapshot the first time we see this (qc, prefix) pair.
  // Also rebuilds on prefix change so the next render reflects the new
  // workspace immediately, not after the next matching cache event.
  if (snapshotPrefixRef.current !== prefixKey) {
    snapshotRef.current = buildSnapshot();
    snapshotPrefixRef.current = prefixKey;
    maybeWarnCardinality(prefixKey, snapshotRef.current.length);
  }

  const subscribe = useCallback(
    (callback: () => void) =>
      qc.getQueryCache().subscribe((event) => {
        // Skip purely structural events (`observerAdded` / `observerRemoved`,
        // `observerResultsUpdated`) — they don't change cache contents and
        // would otherwise trigger a redundant `findAll` walk.
        if (!isCacheDataEvent(event.type)) return;
        if (!keyHasPrefix(event.query.queryKey as readonly unknown[], prefix)) return;
        const fresh = buildSnapshot();
        if (snapshotsAreIdentical(snapshotRef.current, fresh)) return;
        snapshotRef.current = fresh;
        // Threshold may be crossed mid-session as cache events accumulate
        // slots — warn here too so a slowly-growing prefix doesn't escape
        // the dev-only signal.
        maybeWarnCardinality(prefixKey, fresh.length);
        callback();
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefix captured via prefixKey
    [qc, prefixKey, buildSnapshot],
  );

  const getSnapshot = useCallback((): readonly TEntity[] => snapshotRef.current ?? (EMPTY as readonly TEntity[]), []);
  // SSR / non-browser fallback. Hydra is client-only today, but supplying
  // a stable empty snapshot here avoids a hard crash if this hook is
  // ever rendered outside the browser (test SSR, future migration).
  const getServerSnapshot = useCallback((): readonly TEntity[] => EMPTY as readonly TEntity[], []);

  const entities = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // `select` is part of the deps so a stable ref keeps the memo cache
  // alive across renders; an unmemoized `select` will recompute, which
  // is the caller's contract (see the JSDoc above).
  const select = options?.select;
  return useMemo(() => (select == null ? (entities as unknown as TSelected) : select(entities)), [entities, select]);
}
